import { useEffect, useState, useMemo } from "react";
import { Search, Filter } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OrderDialog from "@/components/OrderDialog";
import RelevanceScore from "@/components/RelevanceScore";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "laptop", label: "Laptops" },
  { id: "phone", label: "Phones" },
  { id: "tablet", label: "Tablets" },
  { id: "monitor", label: "Monitors" },
  { id: "accessory", label: "Accessories" },
];

export default function Catalogue() {
  const { persona } = usePersona();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [personaFilter, setPersonaFilter] = useState(true);
  const [orderDevice, setOrderDevice] = useState(null);
  const [relevanceMap, setRelevanceMap] = useState({});

  useEffect(() => {
    setLoading(true);
    endpoints.devices({
      persona: personaFilter ? persona.id : undefined,
      category,
      q: q || undefined,
    }).then((d) => { setDevices(d); setLoading(false); }).catch(() => setLoading(false));
  }, [category, q, personaFilter, persona.id]);

  // Fetch relevance scores in bulk whenever the persona or category changes
  useEffect(() => {
    endpoints.relevanceBulk(persona.id, category === "all" ? undefined : category)
      .then((res) => {
        const map = {};
        for (const r of res.items || []) {
          map[r.device_id] = r;
        }
        setRelevanceMap(map);
      })
      .catch(() => setRelevanceMap({}));
  }, [persona.id, category]);

  const relevanceReady = useMemo(() => Object.keys(relevanceMap).length > 0, [relevanceMap]);

  return (
    <div className="space-y-6" data-testid="catalogue-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Device Catalog</div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">
            {personaFilter ? `Recommended for ${persona.name}` : "All available devices"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">Every order automatically generates a ServiceNow request for IT fulfillment.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="toggle-persona-filter"
            variant={personaFilter ? "default" : "outline"}
            onClick={() => setPersonaFilter(!personaFilter)}
            className={personaFilter ? "bg-[#0033A0] hover:bg-[#002573] text-white" : ""}
          >
            <Filter size={14} className="mr-2" />
            {personaFilter ? `${persona.name} only` : "Show all"}
          </Button>
        </div>
      </div>

      <Card className="p-4 border-slate-200 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <div className="relative flex-1 max-w-lg">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="catalogue-search"
              placeholder="Search by device name or brand"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-slate-50 border-slate-200"
            />
          </div>
          <Tabs value={category} onValueChange={setCategory} data-testid="catalogue-tabs">
            <TabsList className="bg-slate-100">
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c.id} value={c.id} data-testid={`tab-${c.id}`} className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="devices-grid">
        {loading && Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-80 w-full rounded-md" />
        ))}

        {!loading && devices.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500 text-sm border border-dashed border-slate-300 rounded-md">
            No devices match your filters.
          </div>
        )}

        {devices.map((d) => (
          <Card
            key={d.id}
            data-testid={`device-card-${d.id}`}
            className="device-card p-0 border-slate-200 shadow-sm overflow-hidden flex flex-col"
          >
            <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
              <img src={d.image_url} alt={d.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{d.brand}</div>
                <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-600 capitalize">{d.category}</Badge>
              </div>
              <div className="font-display font-bold text-slate-900 mt-1 leading-tight">{d.name}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{d.description}</div>
              <ul className="mt-2 space-y-0.5 flex-1">
                {d.specs.slice(0, 3).map((s) => (
                  <li key={s} className="text-[11px] text-slate-600 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-[#0033A0]" /> {s}
                  </li>
                ))}
              </ul>
              <RelevanceScore data={relevanceMap[d.id]} personaName={persona.name} />
              <div
                data-testid={`approval-chip-${d.id}`}
                className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-semibold w-fit ${
                  d.pre_approved
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
                title={d.pre_approved
                  ? `Pre-approved for ${persona.name} — no manager approval needed`
                  : `This device is outside your ${persona.name} persona's pre-approved set — a manager approval email will be sent`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${d.pre_approved ? "bg-emerald-500" : "bg-amber-500"}`} />
                {d.pre_approved ? "Pre-approved for your persona" : "Needs manager approval"}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-slate-500">Est. billback</div>
                  <div className="font-display font-bold text-slate-900">${d.price.toLocaleString()}</div>
                </div>
                <div className="text-xs">
                  <span className={`inline-flex items-center gap-1 ${d.stock === "In Stock" ? "text-green-700" : d.stock === "Limited" ? "text-amber-700" : "text-red-700"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${d.stock === "In Stock" ? "bg-green-500" : d.stock === "Limited" ? "bg-amber-500" : "bg-red-500"}`} />
                    {d.stock}
                  </span>
                </div>
              </div>
              <Button
                data-testid={`order-btn-${d.id}`}
                onClick={() => setOrderDevice(d)}
                disabled={d.stock === "Out of Stock"}
                className="mt-3 bg-[#0033A0] hover:bg-[#002573] text-white w-full"
              >
                Order Device
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <OrderDialog device={orderDevice} onClose={() => setOrderDevice(null)} onSuccess={() => setOrderDevice(null)} />
    </div>
  );
}
